import React, { useCallback, useContext, useState, useMemo } from 'react';
import { Star, ExternalLink } from 'lucide-react';
import { Card, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { PbsIcon } from '../common/icons';
import { LinkCard } from "../../types/home/links";
import { addFavoriteLink, removeFavoriteLink } from "../../services/mysite/MySiteHome";
import { useApiClient } from '../../context/ApiContext';
import { CurrentUserContext } from '../../context/CurrentUserContext';
import { useTheme } from '../../context/ThemeContext';

interface ILinkCardProps {
    link: LinkCard,
    favorite?: boolean
}

const LinkCardItem: React.FC<ILinkCardProps> = ({ link, favorite }) => {
    const [isFavorite, setIsFavorite] = useState(favorite);
    const api = useApiClient();
    const { currentUser, isOnBehalfOf, accessControl } = useContext(CurrentUserContext);
    const { isDarkMode } = useTheme();

    const disabled = isOnBehalfOf && !accessControl.canWrite('impersonation');
    const openLink = useCallback(() => {
        if (disabled) return;
        window.location.href = link.url;
    }, [link.url, disabled]);

    const openLinkNew = useCallback(() => {
        window.open(link.url, '_blank');
    }, [link.url]);

    const currentColor = useMemo(() => {
        const seed = link.title ? link.title.split('').reduce((a, b) => {
            a = ((a << 5) - a) + b.charCodeAt(0);
            return a & a;
        }, 0) : 0;
        const hue = Math.abs(seed) % 360;
        return `hsl(${hue}, 60%, ${isDarkMode ? '52%' : '30%'})`;
    }, [link.title, isDarkMode]);

    const handleFavoriteToggle = React.useCallback(async () => {
        try {
            if (isFavorite) {
                await removeFavoriteLink(currentUser.LoginName, link.title, api);
            } else {
                await addFavoriteLink(currentUser.LoginName, link, api);
            }
            setIsFavorite(!isFavorite);
        } catch (error) {
            console.error('Error updating favorite status:', error);
        }
    }, [api, currentUser.LoginName, link, isFavorite]);

    return (
        <Card className="m-2 h-28 overflow-hidden flex flex-row" style={{ borderColor: currentColor }}>
            <div className="w-1 rounded-l-lg flex-shrink-0" style={{ backgroundColor: currentColor }} />
            <div className="flex-1 min-w-0">
                <CardHeader className="p-3 pb-0">
                    <div className="flex items-center justify-between w-full gap-2">
                        <span
                            className={
                                disabled 
                                ?
                                "text-[10.5pt] font-medium text-muted-foreground  overflow-hidden text-ellipsis line-clamp-1 flex-1"
                                :
                                "text-[10.5pt] font-medium cursor-pointer overflow-hidden text-ellipsis line-clamp-1 flex-1"
                            }
                            onClick={openLink}
                            title={link.title}
                        >
                            <span style={{ color: currentColor, position: 'relative', top: '2px', marginRight: '6px', display: 'inline-flex' }}>
                                <PbsIcon iconName={link.icon} size={14} />
                            </span>
                            {link.title}
                        </span>
                        <div className="flex items-center gap-1 flex-none">
                            <Button variant="ghost" size="icon" className="h-7 w-7 p-0" onClick={openLinkNew} title="Open In New Window" disabled={disabled}>
                                <ExternalLink className="h-4 w-4 text-muted-foreground" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 p-0" onClick={handleFavoriteToggle} disabled={disabled}>
                                {isFavorite ? (
                                    <Star className="h-4 w-4" fill="currentColor" style={{ color: currentColor }} aria-label="Remove from Favorite" />
                                ) : (
                                    <Star className="h-4 w-4 text-muted-foreground" aria-label="Mark as Favorite" />
                                )}
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <hr className="border-t border-border mx-3 my-1" />
                <Tooltip>
                    <TooltipTrigger asChild>
                        <p className="px-3 text-sm text-muted-foreground overflow-hidden text-ellipsis line-clamp-2 cursor-default">
                            {link.description}
                        </p>
                    </TooltipTrigger>
                    <TooltipContent>{link.description}</TooltipContent>
                </Tooltip>
            </div>
        </Card>
    );
}

export default LinkCardItem;
